import { IonContent, IonIcon, IonPage, IonSpinner } from '@ionic/react';
import { addOutline } from 'ionicons/icons';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { AutomationCard } from '../../components/automation/AutomationCard';
import useAutomationWorkflows from '../../hooks/useAutomationWorkflows';
import isElectron from '../../utils/isElectron';
import './Automations.scss';

const EMPTY_HINTS = [
  'Watch a folder',
  'Tag on arrival',
  'Build a crate',
  'Export to Serato',
];

const AutomationsPage: React.FC = () => {
  const history = useHistory();
  const { automations, loading, save } = useAutomationWorkflows();

  if (!isElectron()) {
    return (
      <IonPage id="automations-page">
        <IonContent className="automations-content ion-padding">
          <p>Automations are available in the Crate Hackers desktop app.</p>
        </IonContent>
      </IonPage>
    );
  }

  const handleToggleEnabled = async (
    automationId: number,
    enabled: boolean,
  ) => {
    const automation = automations.find((item) => item.id === automationId);
    if (!automation) return;
    await save({
      id: automation.id,
      name: automation.name,
      trigger: automation.trigger,
      graph: automation.graph,
      enabled,
    });
  };

  return (
    <IonPage id="automations-page">
      <IonContent className="automations-content">
        <div className="automations-shell">
          <header className="automations-header">
            <h1>Your automations</h1>
            <button
              type="button"
              className="automation-primary-button"
              onClick={() => history.push('/automations/new')}
            >
              <IonIcon icon={addOutline} aria-hidden />
              New automation
            </button>
          </header>

          {loading ? (
            <div className="automations-loading">
              <IonSpinner name="crescent" />
            </div>
          ) : automations.length === 0 ? (
            <div className="automations-empty">
              <img
                src="/assets/flame-icon-white.svg"
                alt=""
                className="automations-empty-flame"
              />
              <h2>Nothing is running for you yet</h2>
              <p>
                An automation watches a folder or waits for you to hit Run, then
                does the boring part: tagging, filing, and building the crate.
              </p>
              <div className="automations-empty-actions">
                <button
                  type="button"
                  className="automation-primary-button"
                  onClick={() => history.push('/automations/new')}
                >
                  Start from blank
                </button>
                <button
                  type="button"
                  className="automation-secondary-button"
                  onClick={() => history.push('/automations/new?tab=templates')}
                >
                  Browse templates
                </button>
              </div>
              <div className="automations-empty-hints">
                {EMPTY_HINTS.map((hint) => (
                  <span key={hint} className="automation-empty-hint">
                    {hint}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="automations-grid">
              {automations.map((automation) => (
                <AutomationCard
                  key={automation.id}
                  automation={automation}
                  onOpen={() => history.push(`/automations/${automation.id}`)}
                  onToggleEnabled={(enabled) =>
                    void handleToggleEnabled(automation.id, enabled)
                  }
                />
              ))}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AutomationsPage;
